<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { UiText, UiIcon, UiInputField, UiSelect, UiSwitch, UiTooltip, UiInfoBox, UiIconButton, UiAvatar } from "@shopexperts/shared";
import type {
  AdminExpertWalletBalance,
  AdminExpertWalletBalancesQueryParams,
  AdminExpertWalletBalancesSort,
} from "@shopexperts/legacy-api";
import { formatCurrency, formatRelativeTime, formatShortDate } from "@/v2Projects/utils/display";
import { getExpertTypeAppearance } from "@/app/components/expert-manager/expertTypeAppearance";
import { expertProfileRoute } from "@/app/components/expert-manager/wallet/transactionParty";
import { useAdminExpertWalletBalances } from "@/app/composables/wallet";
import { LIABILITY_COLORS } from "./walletStatisticsDisplay";

const props = defineProps<{
  active: boolean;
  currency?: string;
}>();

const emit = defineEmits<{
  viewTransactions: [organizationId: number];
}>();

const sort = defineModel<AdminExpertWalletBalancesSort>("sort", { default: "total_owed" });
const direction = defineModel<"asc" | "desc">("direction", { default: "desc" });
const includeSettled = defineModel<boolean>("includeSettled", { default: false });

const search = ref("");
const debouncedSearch = ref("");
let searchTimer: ReturnType<typeof setTimeout> | null = null;

watch(search, (next) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    debouncedSearch.value = next.trim();
  }, 300);
});

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
});

const organizationType = ref<string>("");
const typeOptions = [
  { label: "All types", value: "" },
  { label: "Freelancers", value: "expert" },
  { label: "Agencies", value: "agency" },
];

const filters = computed<AdminExpertWalletBalancesQueryParams>(() => ({
  sort: sort.value,
  direction: direction.value,
  ...(debouncedSearch.value ? { search: debouncedSearch.value } : {}),
  ...(includeSettled.value ? { owed_only: false } : {}),
  ...(organizationType.value ? { organization_type: organizationType.value as "expert" | "agency" } : {}),
  ...(props.currency ? { currency: props.currency } : {}),
}));

const enabled = computed(() => props.active);

const { balances, meta, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
  useAdminExpertWalletBalances({ filters, enabled });

const hasActiveFilters = computed(
  () => debouncedSearch.value !== "" || organizationType.value !== "" || includeSettled.value,
);

interface SortableColumn {
  key: AdminExpertWalletBalancesSort;
  label: string;
  hint?: string;
}

const columns: SortableColumn[] = [
  { key: "available", label: "Available", hint: "Payable to the expert right now." },
  { key: "hold", label: "On hold", hint: "Earned but held until project completion or admin release." },
  { key: "total_owed", label: "Total owed", hint: "Available + on hold." },
  { key: "lifetime_net", label: "Lifetime net", hint: "All-time net earnings (gross below)." },
];

function toggleSort(key: AdminExpertWalletBalancesSort) {
  if (sort.value === key) {
    direction.value = direction.value === "desc" ? "asc" : "desc";
    return;
  }
  sort.value = key;
  direction.value = "desc";
}

interface BalanceRow extends AdminExpertWalletBalance {
  displayName: string;
  typeLabel: string;
  avatarRingClass: string;
  availablePct: number;
}

const rows = computed<BalanceRow[]>(() =>
  balances.value.map((balance) => {
    const owed = Math.max(balance.total_owed_cents, 0);
    return {
      ...balance,
      displayName: balance.expert?.name ?? balance.organization_name ?? `Organization #${balance.organization_id}`,
      typeLabel: balance.organization_type === "agency" ? "Agency" : "Freelancer",
      avatarRingClass: getExpertTypeAppearance(
        balance.organization_type === "agency" ? "agency" : "freelancer",
      ).avatarRingClass,
      availablePct: owed > 0 ? (Math.max(balance.available_balance_cents, 0) / owed) * 100 : 0,
    };
  }),
);

const router = useRouter();

function openExpert(row: BalanceRow) {
  if (!row.expert) return;
  router.push(expertProfileRoute(row.expert.id));
}

// Infinite scroll observer
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: "200px" },
  );
  observer.observe(el);
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

const gridClass =
  "grid grid-cols-[minmax(0,2.1fr)_100px_100px_120px_110px_76px_40px] items-center gap-3 px-4";

function amountClass(cents: number, positiveClass: string) {
  if (cents < 0) return "text-rose-600";
  if (cents === 0) return "text-gray-300";
  return positiveClass;
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <!-- Header + filtered-set totals -->
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <UiText as="h2" variant="h6" class="font-semibold" text="Expert balances" />
        <UiText
          as="p"
          variant="p-small"
          class="text-gray-500"
          text="Everyone the company currently owes, with drill-downs into their wallets."
        />
      </div>
      <UiText v-if="meta" as="p" variant="p-small" class="text-gray-500 tabular-nums">
        {{ meta.total }} expert{{ meta.total === 1 ? "" : "s" }} ·
        <span class="font-semibold text-se-dark">{{ formatCurrency(meta.total_owed_cents, currency) }}</span> owed ·
        <span :class="LIABILITY_COLORS.available.text">{{ formatCurrency(meta.available_balance_cents, currency) }}</span> available ·
        <span :class="LIABILITY_COLORS.hold.text">{{ formatCurrency(meta.hold_balance_cents, currency) }}</span> on hold
      </UiText>
    </div>

    <!-- Toolbar -->
    <div class="flex items-center gap-3">
      <UiInputField
        id="admin-wallet-balances-search"
        v-model="search"
        placeholder="Search by name or email..."
        icon="search"
        size="md"
        class="max-w-sm flex-1"
      />
      <div class="w-44 shrink-0">
        <UiSelect v-model="organizationType" :options="typeOptions" placeholder="All types" />
      </div>
      <UiTooltip content="Include experts with settled (zero) or negative balances.">
        <div class="flex shrink-0 items-center whitespace-nowrap">
          <UiSwitch v-model="includeSettled" size="sm" label="Include settled" />
        </div>
      </UiTooltip>
    </div>

    <!-- Error -->
    <UiInfoBox v-if="error" variant="error" title="Couldn't load expert balances">
      <UiText as="span" variant="p-small">
        <span>Something went wrong loading the balances. Refresh to try again.</span>
      </UiText>
    </UiInfoBox>

    <!-- Loading -->
    <div v-else-if="isLoading" class="flex flex-col gap-3">
      <div v-for="n in 5" :key="n" class="h-16 animate-pulse rounded-xl bg-se-gray-100" />
    </div>

    <!-- Empty -->
    <div
      v-else-if="rows.length === 0"
      class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-se-gray-300 py-16 text-center"
    >
      <div class="flex h-12 w-12 items-center justify-center rounded-full bg-se-gray-100">
        <UiIcon name="wallet" class="h-6 w-6 text-se-gray-500" />
      </div>
      <UiText
        as="h4"
        variant="h6"
        class="font-semibold"
        :text="hasActiveFilters ? 'No experts match your filters' : 'No experts are owed money'"
      />
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="hasActiveFilters
          ? 'Try adjusting or clearing the search and filters above.'
          : 'Expert balances will appear here as wallets accrue earnings.'"
      />
    </div>

    <!-- List (UiCard shell, minus its content padding which would band under the table) -->
    <div v-else class="overflow-hidden rounded-xl border border-se-gray-300 bg-se-white">
      <div class="overflow-x-auto">
          <div class="min-w-[820px]">
            <!-- Column headers double as sort controls -->
            <div :class="gridClass" class="border-b border-se-gray-200 bg-se-gray-100/60 py-2.5">
              <UiText
                as="span"
                variant="caption-normal"
                class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400"
                text="Expert"
              />
              <template v-for="column in columns" :key="column.key">
                <UiTooltip :content="column.hint ?? ''">
                  <button
                    type="button"
                    class="flex cursor-pointer items-center justify-end gap-1 text-right text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors"
                    :class="sort === column.key ? 'text-se-dark' : 'text-gray-400 hover:text-gray-600'"
                    @click="toggleSort(column.key)"
                  >
                    {{ column.label }}
                    <UiIcon
                      name="chevron-down"
                      class="h-3 w-3 transition-transform"
                      :class="[
                        sort === column.key ? 'opacity-100' : 'opacity-0',
                        sort === column.key && direction === 'asc' ? 'rotate-180' : '',
                      ]"
                    />
                  </button>
                </UiTooltip>
              </template>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-right text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400"
                text="Last txn"
              />
              <span />
            </div>

            <ul class="divide-y divide-se-gray-200">
              <li
                v-for="row in rows"
                :key="row.wallet_id"
                :class="[gridClass, row.expert ? 'cursor-pointer hover:bg-gray-50' : '']"
                class="group py-3 transition-colors"
                @click="openExpert(row)"
              >
                <!-- Expert: a real button so the profile opens from the keyboard;
                     the li's click stays as a pointer-only convenience. -->
                <component
                  :is="row.expert ? 'button' : 'div'"
                  :type="row.expert ? 'button' : undefined"
                  class="flex min-w-0 cursor-[inherit] items-center gap-3 rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300"
                  @click.stop="openExpert(row)"
                >
                  <UiAvatar
                    :name="row.displayName"
                    :url="row.expert?.avatar ?? undefined"
                    size="xxs"
                    :responsive="false"
                    color-coded
                    class="shrink-0 ring-2 ring-offset-1 ring-offset-white"
                    :class="row.avatarRingClass"
                  />
                  <div class="flex min-w-0 flex-col">
                    <span class="truncate text-sm font-medium text-se-dark group-hover:underline">
                      {{ row.displayName }}
                    </span>
                    <span class="truncate text-xs text-gray-500">
                      {{ row.typeLabel }}<template v-if="row.expert?.email"> · {{ row.expert.email }}</template>
                    </span>
                  </div>
                </component>

                <!-- Available -->
                <span class="text-right text-sm tabular-nums" :class="amountClass(row.available_balance_cents, LIABILITY_COLORS.available.text)">
                  {{ formatCurrency(row.available_balance_cents, row.currency) }}
                </span>

                <!-- On hold -->
                <span class="text-right text-sm tabular-nums" :class="amountClass(row.hold_balance_cents, LIABILITY_COLORS.hold.text)">
                  {{ formatCurrency(row.hold_balance_cents, row.currency) }}
                </span>

                <!-- Total owed + composition -->
                <div class="flex flex-col items-end gap-1">
                  <span class="text-sm font-semibold tabular-nums" :class="row.total_owed_cents < 0 ? 'text-rose-600' : 'text-se-dark'">
                    {{ formatCurrency(row.total_owed_cents, row.currency) }}
                  </span>
                  <span v-if="row.total_owed_cents > 0" class="flex h-1 w-16 overflow-hidden rounded-full bg-gray-100">
                    <span class="h-full" :class="LIABILITY_COLORS.available.bar" :style="{ width: `${row.availablePct}%` }" />
                    <span class="h-full flex-1" :class="LIABILITY_COLORS.hold.bar" />
                  </span>
                </div>

                <!-- Lifetime -->
                <div class="flex flex-col items-end">
                  <span class="text-sm tabular-nums text-se-dark">{{ formatCurrency(row.lifetime_net_cents, row.currency) }}</span>
                  <span class="text-[11px] tabular-nums text-gray-400">of {{ formatCurrency(row.lifetime_gross_cents, row.currency) }}</span>
                </div>

                <!-- Last activity + actions -->
                <UiTooltip :content="row.last_transaction_at ? formatShortDate(row.last_transaction_at) : 'No transactions yet'">
                  <span class="text-right text-xs text-gray-500">
                    {{ row.last_transaction_at ? formatRelativeTime(row.last_transaction_at) : "—" }}
                  </span>
                </UiTooltip>
                <div @click.stop>
                  <UiTooltip content="View wallet transactions">
                    <UiIconButton
                      icon="wallet"
                      aria-label="View wallet transactions"
                      @click="emit('viewTransactions', row.organization_id)"
                    />
                  </UiTooltip>
                </div>
              </li>
            </ul>
        </div>
      </div>
    </div>

    <div ref="sentinel" class="h-1" />

    <div v-if="isFetchingNextPage" class="py-3 text-center">
      <UiText as="p" variant="p-small" class="text-se-gray-500" text="Loading more…" />
    </div>
  </section>
</template>
